import React from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import "./LinkCard.scss";

const LinkCard = ({
  title,
  href,
  image,
  emoji,
  description,
  secondaryHref,
  secondaryText = "Google Maps",
  tag,
  phone,
  website,
  tags,
}) => {
  const data = useStaticQuery(graphql`
    query LinkCardImages {
      allFile(filter: { sourceInstanceName: { eq: "cards-images" } }) {
        nodes {
          id
          relativePath
          childImageSharp {
            gatsbyImageData(
              width: 480
              placeholder: DOMINANT_COLOR
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
  `);

  const imageMap = React.useMemo(() => {
    const nodes = data?.allFile?.nodes || [];
    const map = new Map();
    for (const node of nodes) {
      const img = node.childImageSharp && getImage(node.childImageSharp);
      if (!img) continue;
      const rel = String(node.relativePath || "").replaceAll("\\", "/");
      if (!rel) continue;
      // JSON paths sont du type "/cards/xxx.ext"
      map.set(`/cards/${rel}`, img);
    }
    return map;
  }, [data]);

  const [imgError, setImgError] = React.useState(false);
  const showImage = image && !imgError;
  const optimizedImage = showImage
    ? imageMap.get(String(image).split(/[?#]/)[0])
    : null;
  let mediaContent;
  if (showImage && optimizedImage) {
    mediaContent = (
      <GatsbyImage
        className="lc-img"
        image={optimizedImage}
        alt={`${title} image`}
      />
    );
  } else if (showImage) {
    mediaContent = (
      // eslint-disable-next-line jsx-a11y/img-redundant-alt
      <img
        className="lc-img"
        src={image}
        alt={`${title} image`}
        loading="lazy"
        onError={() => setImgError(true)}
      />
    );
  } else {
    mediaContent = (
      <div className="lc-placeholder" aria-hidden="true">
        {emoji || "🔗"}
      </div>
    );
  }
  const mapTagToDisplay = (t) => {
    if (!t) return { text: "", display: "" };
    const raw = String(t).trim();
    // Replace "Coup de coeur/cœur" (FR), "favourite/favorite" (EN),
    // "colpo di fulmine" (IT) with a heart emoji on the badge
    const isCoupDeCoeur =
      /coup\s+de\s+c(?:œur|oeur)/i.test(raw) ||
      /favou?rite/i.test(raw) ||
      /colpo\s+di\s+fulmine/i.test(raw) ||
      /colpu\s+di\s+core/i.test(raw);
    if (isCoupDeCoeur) return { text: raw, display: "❤️" };
    // Map proximity/walkable to a walking person emoji
    const isWalkable =
      /proche[\s\S]*pied/i.test(raw) ||
      /close[\s\S]*walk/i.test(raw) ||
      /vicin[oa][\s\S]*piedi/i.test(raw) ||
      /\bpedi\b/i.test(raw);
    if (isWalkable) return { text: raw, display: "🚶" };
    // Street-food / food-truck style (support various hyphens, including NBSP hyphen)
    const isStreetFood = (() => {
      const lower = raw.toLowerCase();
      const hyphenAsSpace = lower.replaceAll(
        /[\u002d\u2010-\u2015\u2212]/g,
        " "
      );
      const collapsed = hyphenAsSpace.replaceAll(/\s+/g, " ");
      return (
        collapsed.includes("street food") || collapsed.includes("food truck")
      );
    })();
    if (isStreetFood) return { text: raw, display: "🍔" };
    // Pizzeria
    const isPizzeria = /\bpizzeria\b/i.test(raw);
    if (isPizzeria) return { text: raw, display: "🍕" };
    // Bord de mer / plage / vue mer / seafront / sea view / fronte mare / vista mare / spiaggia
    const isSeaSide = (() => {
      const lower = raw.toLowerCase();
      const hyphenAsSpace = lower.replaceAll(/[\u002d\u2010-\u2015\u2212]/g, " ");
      const collapsed = hyphenAsSpace.replaceAll(/\s+/g, " ").trim();
      if (
        collapsed.includes("bord de mer") ||
        collapsed.includes("vue mer") ||
        collapsed.includes("seafront") ||
        collapsed.includes("sea view") ||
        collapsed.includes("fronte mare") ||
        collapsed.includes("vista mare")
      ) {
        return true;
      }
      const words = new Set(collapsed.split(" "));
      if (words.has("plage") || words.has("beach") || words.has("spiaggia")) {
        return true;
      }
      return false;
    })();
    if (isSeaSide) return { text: raw, display: "🏖️" };
    // Italien / Italian(o/a)
    const isItalian =
      /\bitalien(ne)?\b/i.test(raw) ||
      /\bitalian[oa]?\b/i.test(raw) ||
      /\btaliana\b/i.test(raw);
    if (isItalian) return { text: raw, display: "🇮🇹" };
    // Café / Bar / Coffee / Caffè (prioritize wine bars)
    if (
      /bar\s*[àa]\s*vins?/i.test(raw) ||
      /wine\s*bar/i.test(raw) ||
      /\benoteca\b/i.test(raw)
    )
      return { text: raw, display: "🍷" };
    if (/(caf[ée]|coffee|caff[èe])/i.test(raw))
      return { text: raw, display: "☕" };
    if (/\bbar\b/i.test(raw)) return { text: raw, display: "🍹" };
    return { text: raw, display: raw };
  };
  return (
    <article className="link-card">
      <a
        className="lc-media"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={title}
      >
        {mediaContent}
        {Array.isArray(tags) && tags.length > 0 ? (
          <span className="lc-tags">
            {tags.map((t) => {
              const { text, display } = mapTagToDisplay(t);
              return (
                <span
                  key={text || String(t)}
                  className="lc-tag"
                  title={text}
                  aria-label={text}
                >
                  {display}
                </span>
              );
            })}
          </span>
        ) : (
          tag &&
          (() => {
            const { text, display } = mapTagToDisplay(tag);
            return (
              <span className="lc-tag" title={text} aria-label={text}>
                {display}
              </span>
            );
          })()
        )}
      </a>
      <div className="lc-body">
        <h4 className="lc-title">
          <a href={href} target="_blank" rel="noopener noreferrer">
            {title}
          </a>
        </h4>
        {description && <p className="lc-desc">{description}</p>}
        {(website || secondaryHref || phone) && (
          <p className="lc-secondary" aria-label={`Actions – ${title}`}>
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Ouvrir le site web – ${title}`}
                title="Site web"
              >
                <svg
                  className="lc-icon lc-icon-globe"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  aria-hidden="true"
                  focusable="false"
                >
                  <g
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18" />
                    <path d="M12 3c2.6 3.2 2.6 14.8 0 18" />
                    <path d="M12 3c-2.6 3.2 -2.6 14.8 0 18" />
                    <path d="M5 8c2.4 1.6 11.6 1.6 14 0" />
                    <path d="M5 16c2.4 -1.6 11.6 -1.6 14 0" />
                  </g>
                </svg>
                <span className="sr-only">Site web</span>
              </a>
            )}
            {secondaryHref && (
              <a
                href={secondaryHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Ouvrir dans Google Maps – ${title}`}
                title="Google Maps"
              >
                <svg
                  className="lc-icon lc-icon-maps"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fill="currentColor"
                    d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z"
                  />
                </svg>
                <span className="sr-only">Google Maps</span>
              </a>
            )}
            {phone && (
              <a
                href={`tel:${String(phone).replaceAll(/\s+/g, "")}`}
                aria-label={`Appeler – ${title}`}
                title={phone}
              >
                <svg
                  className="lc-icon lc-icon-phone"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fill="currentColor"
                    d="M6.6 2.9l2.5-.7c.5-.1 1 .1 1.2.6l1.3 3.1c.2.5.1 1-.3 1.3l-1.6 1.3c.9 1.8 2.3 3.2 4 4l1.3-1.6c.3-.4.9-.5 1.3-.3l3.1 1.3c.5.2.7.7.6 1.2l-.7 2.5c-.1.5-.5.9-1 1-2.3.3-6.8-.5-10.7-4.4C5.1 9.7 4.3 5.2 4.6 2.9c.1-.5.5-.9 1-1z"
                  />
                </svg>
                <span className="sr-only">{phone}</span>
              </a>
            )}
          </p>
        )}
      </div>
    </article>
  );
};

LinkCard.propTypes = {
  title: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  image: PropTypes.string,
  emoji: PropTypes.string,
  description: PropTypes.string,
  secondaryHref: PropTypes.string,
  secondaryText: PropTypes.string,
  tag: PropTypes.string,
  phone: PropTypes.string,
  website: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
};

export default LinkCard;
